import { computed, effect, inject, Injectable, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { ActivatedRoute, PRIMARY_OUTLET, Router, UrlTree } from '@angular/router';
import { defer, EMPTY, firstValueFrom, map, Observable, of } from 'rxjs';
import { catchError, filter, finalize, take, tap } from 'rxjs/operators';
import { UrlSegment } from '../types/enums/url-segment.enum';
import {
  ChangePasswordRequestBody,
  CheckConfirmEmailTokenRequestBody,
  CheckResetPasswordTokenRequestBody,
  ConfirmEmailSetPasswordRequestBody,
  ForgotPasswordRequestBody,
  LoginRequestBody,
  LoginResponse,
  ResetPasswordRequestBody
} from '../types/models/auth/auth.model';
import { UpdateUserDetailsRequestBody, UserDTO } from '../types/models/user/user-dto.model';
import { UserProfileMergeFields } from '../types/models/user/user-profile.model';
import { User } from '../types/models/user/user.model';
import { UserFactory } from './factories/user.factory';
import { AuthHttpService } from './http/auth-http.service';
import { LocalStorageService } from './local-storage.service';
import { LanguageService } from './shared/language.service';
import { LoaderService } from './shared/loader.service';

/**
 * AuthService - Session state, tokens, and auth-related orchestration.
 *
 * Delegates HTTP to {@link AuthHttpService}. Persists tokens via {@link LocalStorageService}, restores the signed-in
 * user on startup when tokens exist, and clears storage on sign-out. Token refresh on 401 is handled in
 * {@link httpErrorInterceptor}, not here.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Guards, layout, sign-in/out flows, and features that need the signed-in user.
 *
 * Architecture:
 * - {@link AuthService}: User model, navigation, loaders
 * - {@link LocalStorageService}: Tokens and related storage helpers
 * - {@link AuthHttpService}: Auth HTTP API
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly authHttpService: AuthHttpService = inject(AuthHttpService);
  private readonly router: Router = inject(Router);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly tokenService: LocalStorageService = inject(LocalStorageService);
  private readonly userFactory: UserFactory = inject(UserFactory);
  private readonly languageService: LanguageService = inject(LanguageService);

  /** Monotonic session boundary: increments on sign-out and on every sign-in/bootstrap, including relogin on the same account. */
  private readonly _sessionGeneration: WritableSignal<number> = signal(0);
  /** Increments on every successful V2 basic-data edit; lets slower in-flight profile GETs detect a newer edit and skip their merge. */
  private readonly _profileRevision: WritableSignal<number> = signal(0);
  private _currentUser: WritableSignal<User> = signal(undefined);

  public isAuthenticated: Signal<boolean> = computed(
    () =>
      (this.currentUser() !== null && this.currentUser() !== undefined) ||
      (this.tokenService.tokens() !== null && this.tokenService.tokens() !== undefined)
  );
  public currentUser: Signal<User> = this._currentUser.asReadonly();
  public sessionGeneration: Signal<number> = this._sessionGeneration.asReadonly();
  public profileRevision: Signal<number> = this._profileRevision.asReadonly();

  public constructor() {
    this.loadAuthData();

    // A token-refresh failure clears tokens directly (RefreshAccessTokenService), without going through
    // signOut/afterSignOutActions. Catch that here so the session actually ends instead of leaving
    // currentUser/sessionGeneration/profileRevision pointing at a session with no tokens. Guarded on
    // `currentUser` still being set so an explicit signOut (which already clears currentUser first) does
    // not run this a second time.
    effect(() => {
      const hasTokens = !!this.tokenService.tokens();
      const hasUser = this._currentUser() !== undefined;
      if (!hasTokens && hasUser) {
        untracked(() => this.endSessionAfterTokenLoss());
      }
    });
  }

  private endSessionAfterTokenLoss(): void {
    this._currentUser.set(undefined);
    this._sessionGeneration.update(generation => generation + 1);
    this._profileRevision.set(0);
    this.languageService.resetExplicitOverride();
    // RefreshAccessTokenService already navigates to `sign-in?redirectUrl=…` (and shows the logged-out
    // toast once that navigation succeeds) before this effect runs. Navigating again would supersede it
    // and drop both, so only navigate when nothing is already taking the user to the sign-in page.
    if (!this.isOnOrNavigatingToSignIn()) {
      void this.router.navigate(['/sign-in']);
    }
  }

  private isOnOrNavigatingToSignIn(): boolean {
    const navigation = this.router.currentNavigation();
    const targetUrl: UrlTree = navigation
      ? (navigation.finalUrl ?? navigation.extractedUrl)
      : this.router.parseUrl(this.router.url);
    return targetUrl.root.children[PRIMARY_OUTLET]?.segments[0]?.path === UrlSegment.SIGN_IN;
  }

  public signIn(body: LoginRequestBody): Observable<LoginResponse> {
    const expectedGeneration = this._sessionGeneration();
    return defer(() => {
      if (expectedGeneration !== this._sessionGeneration()) {
        return EMPTY;
      }
      this.loaderService.startLoading();
      return this.authHttpService.login(body).pipe(
        filter(() => expectedGeneration === this._sessionGeneration()),
        tap(response => {
          this.tokenService.setTokens(response.tokens);
          this.languageService.resetExplicitOverride();
          this.setCurrentUser(response.user);
          const redirectUrl: string = this.activatedRoute.snapshot.queryParams['redirectUrl'] || '/signed-in-redirect';
          void this.router.navigateByUrl(redirectUrl);
        }),
        finalize(() => this.loaderService.stopLoading())
      );
    });
  }

  public signOut(): Observable<unknown> {
    const expectedGeneration = this._sessionGeneration();
    return defer(() => {
      if (expectedGeneration !== this._sessionGeneration()) {
        return EMPTY;
      }
      return this.authHttpService.logout().pipe(
        finalize(() => {
          if (expectedGeneration === this._sessionGeneration()) {
            this.afterSignOutActions();
          }
        }),
        catchError(() => of(undefined))
      );
    });
  }

  private afterSignOutActions(): void {
    this._currentUser.set(undefined);
    this._sessionGeneration.update(generation => generation + 1);
    this._profileRevision.set(0);
    this.languageService.resetExplicitOverride();
    this.tokenService.clearLocalStorage();

    void this.router.navigate(['/sign-in']);
  }

  public forgotPassword(body: ForgotPasswordRequestBody): Observable<unknown> {
    return this.authHttpService.forgotPassword({ email: body.email.trim().toLocaleLowerCase() });
  }

  public checkConfirmEmailToken(body: CheckConfirmEmailTokenRequestBody): Observable<unknown> {
    return this.authHttpService.checkConfirmEmailToken(body);
  }

  public checkResetPasswordToken(body: CheckResetPasswordTokenRequestBody): Promise<void> {
    return firstValueFrom(this.authHttpService.checkResetPasswordToken(body));
  }

  public resetPassword(body: ResetPasswordRequestBody): Promise<void> {
    return firstValueFrom(this.authHttpService.resetPassword(body));
  }

  /** Changes the signed-in user's password (`password` = current, `newPassword` = new one). */
  public changePassword(body: ChangePasswordRequestBody): Observable<void> {
    return this.authHttpService.changePassword(body);
  }

  public confirmEmailSetPassword(
    body: ConfirmEmailSetPasswordRequestBody
  ): Signal<{ success: boolean; error: boolean }> {
    const resultSignal: WritableSignal<{ success: boolean; error: boolean }> = signal({
      success: false,
      error: false
    });

    this.authHttpService.confirmEmailSetPassword(body).subscribe({
      next: () => resultSignal.set({ success: true, error: false }),
      error: () => resultSignal.set({ success: false, error: true })
    });

    return resultSignal;
  }

  private loadAuthData(): void {
    const tokens = this.tokenService.tokens();
    if (tokens?.accessToken && tokens?.refreshToken) {
      const expectedGeneration = this._sessionGeneration();
      this.authHttpService
        .getSignedInUser()
        .pipe(take(1))
        .subscribe({
          next: user => {
            // A sign-out (or sign-in as someone else) may have happened while this bootstrap call was
            // in flight; only apply it if the session boundary is still the one it started in and no
            // user has been set in the meantime.
            if (
              expectedGeneration !== this._sessionGeneration() ||
              this._currentUser() !== undefined ||
              !this.tokenService.tokens()?.accessToken
            ) {
              return;
            }
            this.setCurrentUser(user);
          },
          error: () => {
            if (expectedGeneration === this._sessionGeneration() && this._currentUser() === undefined) {
              this.tokenService.clearLocalStorage();
            }
          }
        });
    } else {
      this.tokenService.clearLocalStorage();
      this._currentUser.set(undefined);
    }
  }

  /** Login/bootstrap entry point. Marks a new session boundary — distinct from sign-out and from any prior session on the same account. */
  public setCurrentUser(user: UserDTO): void {
    if (!user) {
      return;
    }

    const userModel = this.userFactory.produce(user);
    this._currentUser.set(userModel);
    this._sessionGeneration.update(generation => generation + 1);
    this._profileRevision.set(0);
  }

  /**
   * Narrow, safe merge of allow-listed own-profile fields into the current full {@link User}.
   * Recreates derived searchable fields via the {@link User} constructor while preserving status/dates/completion.
   * Rejects the merge (returns `false`) if the session or actor changed, or a newer V2 edit has already
   * landed, since then this merge's data would be stale.
   */
  public applyProfileFields(
    fields: UserProfileMergeFields,
    actorId: string,
    expectedSessionGeneration: number,
    expectedProfileRevision: number
  ): boolean {
    if (expectedSessionGeneration !== this._sessionGeneration()) {
      return false;
    }
    if (expectedProfileRevision !== this._profileRevision()) {
      return false;
    }

    const current = this._currentUser();
    if (!current || current.id !== actorId) {
      return false;
    }

    this._currentUser.set(
      new User({
        ...current,
        ...fields,
        address: fields.address ?? undefined,
        phoneNumbers: fields.phoneNumbers ?? undefined,
        notes: fields.notes ?? undefined
      })
    );
    return true;
  }

  public updateCurrentUser(request: UpdateUserDetailsRequestBody): Observable<User> {
    const expectedGeneration = this._sessionGeneration();
    const actorId = this._currentUser()?.id;
    return defer(() => {
      const isCurrentSession = (): boolean =>
        expectedGeneration === this._sessionGeneration() &&
        actorId === this._currentUser()?.id &&
        !!actorId &&
        !!this.tokenService.tokens()?.accessToken;
      if (!isCurrentSession()) {
        return EMPTY;
      }
      this.loaderService.startLoading();
      return this.authHttpService.updateCurrentUser(request).pipe(
        filter(isCurrentSession),
        map(editedUser => {
          if (editedUser.id !== actorId) {
            throw new Error('The basic-data response does not belong to the current account.');
          }
          const user = this.userFactory.produce(editedUser);
          this._currentUser.set(user);
          this._profileRevision.update(revision => revision + 1);
          return user;
        }),
        finalize(() => this.loaderService.stopLoading())
      );
    });
  }
}
